import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Phone, Mail, StickyNote, Search, History as HistoryIcon } from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';
import MessageLogDialog from '../components/MessageLogDialog';

const API = `${getBackendUrl()}/api`;

const TYPE_META = {
  sms:   { icon: MessageSquare, color: 'text-blue-600',    bg: 'bg-blue-50' },
  call:  { icon: Phone,         color: 'text-emerald-600', bg: 'bg-emerald-50' },
  email: { icon: Mail,          color: 'text-orange-600',  bg: 'bg-orange-50' },
  note:  { icon: StickyNote,    color: 'text-gray-600',    bg: 'bg-gray-100' },
};

function initials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Toronto', month: 'short', day: 'numeric' });
}

export default function MessageLog({ user }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openContact, setOpenContact] = useState(null);

  const fetchConversations = async () => {
    try {
      const { data } = await axios.get(`${API}/communications/conversations`);
      setConversations(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load message log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConversations(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return conversations;
    return conversations.filter((c) =>
      (c.other_name || '').toLowerCase().includes(q) ||
      (c.last_message || '').toLowerCase().includes(q) ||
      (c.last_subject || '').toLowerCase().includes(q)
    );
  }, [conversations, search]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-28">
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-3">
          <HistoryIcon className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Message Log</h1>
        </div>
        <p className="text-sm text-gray-500 mb-4">Copies of texts, calls, and emails you've sent or received through the app.</p>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or message…"
            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            data-testid="message-log-search"
          />
        </div>

        {loading ? (
          <div className="text-center text-sm text-gray-500 py-8">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-16">
            <HistoryIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            No messages yet. Send a text, call, or email from Connections — copies will appear here.
          </div>
        ) : (
          <div className="space-y-2" data-testid="conversations-list">
            {filtered.map((c) => {
              const lastType = c.last_type || 'note';
              const meta = TYPE_META[lastType] || TYPE_META.note;
              const Icon = meta.icon;
              const youSent = c.last_from === user.id;
              return (
                <button
                  key={c.other_user_id}
                  onClick={() => setOpenContact({ id: c.other_user_id, name: c.other_name })}
                  className="w-full bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex items-center gap-3 hover:shadow-md transition text-left"
                  data-testid={`conv-${c.other_user_id}`}
                >
                  <div className="relative">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold flex items-center justify-center text-sm">
                      {initials(c.other_name)}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${meta.bg} flex items-center justify-center border-2 border-white`}>
                      <Icon className={`w-3 h-3 ${meta.color}`} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold text-gray-900 truncate">{c.other_name || 'Unknown'}</p>
                      <span className="text-[11px] text-gray-500 flex-shrink-0">{timeAgo(c.last_at)}</span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {youSent && <span className="text-gray-400">You: </span>}
                      {c.last_subject ? <span className="font-semibold">{c.last_subject} — </span> : null}
                      {c.last_message || <span className="italic text-gray-400">No content</span>}
                    </p>
                  </div>
                  {c.unread_count > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] px-1.5 flex items-center justify-center">
                      {c.unread_count > 99 ? '99+' : c.unread_count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {openContact && (
        <MessageLogDialog
          contact={openContact}
          currentUserId={user.id}
          onClose={() => { setOpenContact(null); fetchConversations(); }}
        />
      )}
    </div>
  );
}
